import { EDITOR_GUTTER_PADDING_LEFT } from '@bangle.io/constants';
import { type EditorEngineContract, useCoreServices } from '@bangle.io/context';
import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo, useSyncExternalStore } from 'react';
import { LinkedMentions } from '../components/backlinks/linked-mentions';
import { LocalGraphSection } from '../graph';
import { EditorSurface } from '../components/editor-surface';
import { NoteNotFoundView } from '../components/feedback/note-not-found-view';
import { WorkspaceNotFoundView } from '../components/feedback/workspace-not-found-view';
import { AppHeader } from '../layout/app-header';
import { PageContentContainer } from '../layout/main-content-container';
import { PropertiesPanel } from '../properties/properties-panel';
import { useCloudWorkspaceAccess } from '../share/use-cloud-workspace-access';

const MAIN_EDITOR_NAME = 'main-editor';

// Horizontal page gutters, with the left side widened for the block handle.
// The editor engine owns its vertical content inset.
const EDITOR_CONTENT_PADDING = `pr-4 md:pr-6 ${EDITOR_GUTTER_PADDING_LEFT}`;

type SaveStatusSource = Pick<
  EditorEngineContract,
  'hasPendingOrFailedSave' | 'subscribeToSaveStatus'
>;

/**
 * Save status lives outside Jotai, so it has to be subscribed to rather than
 * read during render: otherwise a save that settles after the file tree
 * dropped the path would never re-render this page.
 */
function useHasPendingSave(
  saveStatus: SaveStatusSource,
  wsPath: string | undefined,
): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      wsPath === undefined
        ? () => {}
        : saveStatus.subscribeToSaveStatus(onStoreChange, wsPath),
    [saveStatus, wsPath],
  );
  const getSnapshot = useCallback(
    () => wsPath !== undefined && saveStatus.hasPendingOrFailedSave(wsPath),
    [saveStatus, wsPath],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

export function PageEditor() {
  const coreServices = useCoreServices();
  const currentWsPath = useAtomValue(
    coreServices.workspaceState.$currentWsPath,
  );
  const currentWsName = useAtomValue(
    coreServices.workspaceState.$currentWsName,
  );
  const $forceReloadCounter = useAtomValue(
    coreServices.editorService.$forceReloadCounter,
  );
  const routeWsPath = useAtomValue(coreServices.navigation.$wsFilePath);
  const routeWsName = useAtomValue(coreServices.navigation.$wsName);
  const { isViewer } = useCloudWorkspaceAccess();
  const hasPendingSave = useHasPendingSave(
    coreServices.editorEngine,
    routeWsPath?.wsPath,
  );
  // A watcher-driven rename/delete removes the route path from the file tree.
  // Keep a dirty editor mounted so its only unsaved copy remains recoverable.
  const editorWsPath =
    currentWsPath ?? (hasPendingSave ? routeWsPath : undefined);

  const editorKey = useMemo(() => {
    return editorWsPath
      ? `editor::${MAIN_EDITOR_NAME}:${editorWsPath.wsPath}:${$forceReloadCounter}:${isViewer ? 'ro' : 'rw'}`
      : `${MAIN_EDITOR_NAME}:${$forceReloadCounter}`;
  }, [editorWsPath, $forceReloadCounter, isViewer]);

  return (
    <>
      <AppHeader />
      <PageContentContainer applyPadding={false}>
        {editorWsPath && currentWsName ? (
          <div data-properties-panel="" className="flex min-h-0 flex-1 flex-col">
            <div className={EDITOR_CONTENT_PADDING}>
              <PropertiesPanel
                wsPath={editorWsPath.wsPath}
                wsName={currentWsName}
                canEdit={!isViewer}
              />
            </div>
            <div className="relative flex-1">
              <EditorSurface
                key={editorKey}
                name={editorKey}
                wsPath={editorWsPath.wsPath}
                className={EDITOR_CONTENT_PADDING}
                readOnly={isViewer}
              />
            </div>
            {/* Clicking the empty space under a short note puts the caret back
                in it, the way every other editor behaves. Without this the
                click lands on the page and leaves focus on the body, so the
                next keystroke — Cmd/Ctrl-A especially — is handled by the
                browser against the whole document instead of the note. */}
            <button
              aria-hidden
              className="min-h-16 flex-1 cursor-text"
              onMouseDown={(event) => {
                event.preventDefault();
                coreServices.editorEngine.focusEditor();
              }}
              tabIndex={-1}
              type="button"
            />
            {/* The editor can outlive the tree entry when a dirty note is
                externally renamed or deleted, so this has no path to render. */}
            {currentWsPath && <LinkedMentions currentWsPath={currentWsPath} />}
            {currentWsPath && (
              <LocalGraphSection currentWsPath={currentWsPath} />
            )}
          </div>
        ) : !currentWsName ? (
          <WorkspaceNotFoundView wsName={routeWsName} />
        ) : (
          // NOTE: It is intentional we are not redirecting to the error page so that we avoid bouncing user
          <NoteNotFoundView />
        )}
      </PageContentContainer>
    </>
  );
}
